const motionSelector = 'video[data-gf-motion-autoplay]';
const controlSelector = '[data-gf-motion-toggle][aria-controls]';
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const trackedVideos = new Set();
const trackedControls = new WeakSet();
const visibleVideos = new WeakSet();
const manuallyPausedVideos = new WeakSet();
const manuallyPlayedVideos = new WeakSet();

function getControlledVideo(control) {
  const id = control.getAttribute('aria-controls');
  const video = id ? document.getElementById(id) : null;
  return video instanceof HTMLVideoElement && video.matches(motionSelector) ? video : null;
}

function getVideoControl(video) {
  if (!video.id) return null;
  for (const control of document.querySelectorAll(controlSelector)) {
    if (control.getAttribute('aria-controls') === video.id) return control;
  }
  return null;
}

function syncControl(video) {
  const control = getVideoControl(video);
  if (!(control instanceof HTMLButtonElement)) return;

  const isPlaying = !video.paused && !video.ended;
  const label = isPlaying ? control.dataset.pauseLabel : control.dataset.playLabel;
  const text = isPlaying ? control.dataset.pauseText : control.dataset.playText;
  if (label) control.setAttribute('aria-label', label);

  const textElement = control.querySelector('[data-gf-motion-toggle-text]');
  if (textElement && text && textElement.textContent !== text) textElement.textContent = text;
}

function pauseVideo(video) {
  video.pause();
  syncControl(video);
}

function playVideo(video) {
  video.muted = true;
  const playback = video.play();
  if (playback && typeof playback.then === 'function') {
    playback.then(() => syncControl(video)).catch(() => syncControl(video));
  } else {
    syncControl(video);
  }
}

function syncVideo(video) {
  if (reducedMotion.matches && document.hidden) manuallyPlayedVideos.delete(video);
  const motionAllowed = reducedMotion.matches
    ? manuallyPlayedVideos.has(video)
    : !manuallyPausedVideos.has(video);

  if (!motionAllowed || document.hidden || !visibleVideos.has(video)) {
    pauseVideo(video);
    return;
  }

  playVideo(video);
}

function registerControl(control) {
  if (!(control instanceof HTMLButtonElement) || trackedControls.has(control)) return;

  const video = getControlledVideo(control);
  if (!video) return;

  trackedControls.add(control);
  control.addEventListener('click', () => {
    if (video.paused || video.ended) {
      manuallyPausedVideos.delete(video);
      manuallyPlayedVideos.add(video);
      playVideo(video);
    } else {
      manuallyPausedVideos.add(video);
      manuallyPlayedVideos.delete(video);
      pauseVideo(video);
    }
  });
  syncControl(video);
}

const visibilityObserver = 'IntersectionObserver' in window
  ? new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visibleVideos.add(entry.target);
          else {
            visibleVideos.delete(entry.target);
            if (reducedMotion.matches) manuallyPlayedVideos.delete(entry.target);
          }
          syncVideo(entry.target);
        }
      },
      { rootMargin: '120px 0px', threshold: 0.01 }
    )
  : null;

function registerVideo(video) {
  if (trackedVideos.has(video)) return;

  trackedVideos.add(video);
  video.addEventListener('play', () => syncControl(video));
  video.addEventListener('pause', () => syncControl(video));
  if (visibilityObserver) visibilityObserver.observe(video);
  else {
    visibleVideos.add(video);
    syncVideo(video);
  }

  const control = getVideoControl(video);
  if (control) registerControl(control);
  syncControl(video);
}

function registerVideos(root) {
  if (root instanceof HTMLVideoElement && root.matches(motionSelector)) registerVideo(root);
  for (const video of root.querySelectorAll?.(motionSelector) ?? []) registerVideo(video);
}

function registerControls(root) {
  if (root instanceof HTMLButtonElement && root.matches(controlSelector)) registerControl(root);
  for (const control of root.querySelectorAll?.(controlSelector) ?? []) registerControl(control);
}

function syncVideos() {
  for (const video of trackedVideos) {
    if (!video.isConnected) {
      visibilityObserver?.unobserve(video);
      visibleVideos.delete(video);
      trackedVideos.delete(video);
      continue;
    }
    syncVideo(video);
  }
}

registerVideos(document);
registerControls(document);

new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      registerVideos(node);
      registerControls(node);
    }
  }
  syncVideos();
}).observe(document.documentElement, { childList: true, subtree: true });

if (typeof reducedMotion.addEventListener === 'function') reducedMotion.addEventListener('change', syncVideos);
else reducedMotion.addListener(syncVideos);

document.addEventListener('visibilitychange', syncVideos);
window.addEventListener('pageshow', syncVideos);
window.addEventListener('pagehide', () => {
  for (const video of trackedVideos) pauseVideo(video);
});
